import { useEffect } from "react";
import { Star, ExternalLink } from "lucide-react";
import { withAmazonTag } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const PRODUCTS = [
  {
    id: "p1",
    category: "Best Air Fryers",
    name: "Cosori Pro II 5.8 Qt Air Fryer",
    blurb:
      "Roomy, quiet, and consistent. Our go-to for two-person households and impromptu dinner parties alike.",
    rating: 5,
    image_url: "https://images.unsplash.com/photo-1626202373052-9cb6c576f0f7?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/B08FB7B3K6?tag=justairfry-20",
  },
  {
    id: "p2",
    category: "Best Air Fryers",
    name: "Ninja Foodi Dual Zone Air Fryer",
    blurb:
      "Two independent baskets. Cook your main and side at the same time — a literal kitchen game-changer.",
    rating: 5,
    image_url: "https://images.unsplash.com/photo-1648438299574-fdd0cfa9d6a9?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/B086CWGCKR?tag=justairfry-20",
  },
  {
    id: "p3",
    category: "Best Air Fryers",
    name: "Instant Vortex Plus 6 Qt",
    blurb:
      "Smart presets and a clear viewing window so you can keep tabs without opening the basket.",
    rating: 4,
    image_url: "https://images.pexels.com/photos/19834442/pexels-photo-19834442.jpeg?auto=compress&cs=tinysrgb&w=800",
    amazon_url: "https://www.amazon.com/dp/B08699S4RB?tag=justairfry-20",
  },
  {
    id: "p4",
    category: "Must-Have Accessories",
    name: "Silicone Air Fryer Liner Set",
    blurb:
      "Reusable, dishwasher-safe liners that save your basket from sticky glazes and burnt cheese.",
    rating: 5,
    image_url: "https://images.unsplash.com/photo-1604908554121-49ed7a716be1?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/B09NLMM3HG?tag=justairfry-20",
  },
  {
    id: "p5",
    category: "Must-Have Accessories",
    name: "OXO Mandoline Slicer",
    blurb:
      "Restaurant-perfect fries and chips in seconds. Worth every penny for the safety guard alone.",
    rating: 5,
    image_url: "https://images.unsplash.com/photo-1583778176476-4a8b02a64c01?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/B00004OCNJ?tag=justairfry-20",
  },
  {
    id: "p6",
    category: "Must-Have Accessories",
    name: "Microplane Premium Zester",
    blurb:
      "The single best $15 you'll spend in your kitchen. Garlic, ginger, parmesan — all transformed.",
    rating: 5,
    image_url: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/B000MWFEV6?tag=justairfry-20",
  },
  {
    id: "p7",
    category: "Cookbooks",
    name: "The Complete Air Fryer Cookbook",
    blurb:
      "550+ tested recipes, gorgeous photos, and time-tested techniques. A coffee-table cookbook you'll actually cook from.",
    rating: 4,
    image_url: "https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/B07P9XGN9T?tag=justairfry-20",
  },
  {
    id: "p8",
    category: "Cookbooks",
    name: "Skinnytaste Air Fryer Dinners",
    blurb:
      "75 healthy, family-friendly weeknight dinners with full nutrition info and meal-prep notes.",
    rating: 5,
    image_url: "https://images.unsplash.com/photo-1532153975070-2e9ab71f1b14?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/0593579941?tag=justairfry-20",
  },
  {
    id: "p9",
    category: "Cookbooks",
    name: "Air Fryer Perfection (America's Test Kitchen)",
    blurb:
      "Science-backed techniques. The book that taught us why patting wings dry matters so much.",
    rating: 5,
    image_url: "https://images.unsplash.com/photo-1495546968767-f0573cca821e?w=800&q=80",
    amazon_url: "https://www.amazon.com/dp/1948703726?tag=justairfry-20",
  },
];

const SECTIONS = ["Best Air Fryers", "Must-Have Accessories", "Cookbooks"];

const StarRow = ({ rating }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        size={14}
        fill={i < rating ? "#C85A32" : "transparent"}
        stroke={i < rating ? "#C85A32" : "#C85A32"}
      />
    ))}
  </div>
);

const ProductCard = ({ p, amazonTag }) => (
  <article
    className="jaf-card rounded-xl overflow-hidden flex flex-col"
    data-testid={`product-card-${p.id}`}
  >
    <div className="aspect-[4/3] overflow-hidden bg-[color:var(--jaf-surface)]">
      <img src={p.image_url} alt={p.name} loading="lazy" className="w-full h-full object-cover" />
    </div>
    <div className="p-6 flex-1 flex flex-col">
      <h3 className="font-serif text-xl leading-snug mb-2">{p.name}</h3>
      <StarRow rating={p.rating} />
      <p className="text-sm mt-3 mb-5 leading-relaxed flex-1" style={{ color: "var(--jaf-text-muted)" }}>
        {p.blurb}
      </p>
      <a
        href={withAmazonTag(p.amazon_url, amazonTag)}
        target="_blank"
        rel="noopener noreferrer sponsored"
        data-testid={`product-amazon-${p.id}`}
        className="jaf-btn-primary inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium"
      >
        Buy on Amazon <ExternalLink size={14} />
      </a>
    </div>
  </article>
);

export default function ResourcesPage() {
  const { settings } = useSettings();
  useEffect(() => {
    document.title = "Resources — JustAirFry";
  }, []);

  return (
    <div data-testid="resources-page">
      {/* Disclaimer bar */}
      <div
        className="text-center py-2.5 text-xs"
        style={{
          backgroundColor: "var(--jaf-surface-alt)",
          color: "var(--jaf-text-muted)",
          borderBottom: "1px solid var(--jaf-border)",
        }}
        data-testid="affiliate-disclaimer"
      >
        Disclosure: As an Amazon Associate, JustAirFry earns from qualifying purchases. We only recommend products we genuinely use and love.
      </div>

      {/* Header */}
      <section className="py-14 md:py-20" style={{ backgroundColor: "var(--jaf-bg)" }}>
        <div className="jaf-container max-w-3xl">
          <span
            className="inline-block text-xs tracking-[0.18em] uppercase font-semibold mb-3"
            style={{ color: "var(--jaf-primary)" }}
          >
            Our resources
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
            Everything you need to air fry like a pro
          </h1>
          <p className="mt-5 text-base sm:text-lg leading-relaxed" style={{ color: "var(--jaf-text-muted)" }}>
            From our favorite air fryers to the little tools we reach for daily — these are the items that actually live on our countertop, not just on a shelf.
          </p>
        </div>
      </section>

      {/* Product sections */}
      {SECTIONS.map((section) => {
        const items = PRODUCTS.filter((p) => p.category === section);
        return (
          <section
            key={section}
            className="py-12 md:py-16"
            data-testid={`section-${section.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="jaf-container">
              <h2 className="font-serif text-3xl sm:text-4xl mb-8">{section}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                {items.map((p) => (
                  <ProductCard key={p.id} p={p} amazonTag={settings.amazon_associate_tag} />
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
